import React, { useCallback, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { UploadFile } from '@mui/icons-material';
import { parseDatasetFile, ParsedDataset } from '../../support/dataImport/parseDataset';
import { resolvePreset } from '../../support/dataImport/presets';
import { WidgetChartType, WidgetSpec } from '../../support/dataImport/presets/types';
import {
  buildGenericWidget,
  buildRawTableWidget,
  GenericAggregate,
  GenericWidgetMapping,
} from '../../support/dataImport/presets/generic';
import { persistWidgets } from '../../support/dataImport/persistWidgets';
import { uploadCustomerFile } from '../../support/customerFiles';
import { SalesforceFileAnswer } from '../../support/assessments';
import WidgetRenderer from './WidgetRenderer';

/**
 * The end-to-end file-import → widgets surface.
 *
 * Drop an Excel/CSV file; it is parsed entirely client-side. If a domain preset (e.g. Cisco inventory)
 * recognises it, its widgets appear automatically; otherwise the generic column-mapper lets the user
 * turn any spreadsheet into a chart. Widgets render live through the dashboard tile renderer. "Save
 * widgets" persists the dataset + a `CustomTile` per chart (see `persistWidgets`) and reports the saved
 * reference back to the caller — which, inside an assessment, is stored as the question's answer.
 */

type SavedRef = {
  datasetId: number;
  presetKey?: string;
  tileIds: number[];
  widgetCount: number;
  /** Set when the raw file was also attached to a Salesforce account (best-effort). */
  salesforceFile?: SalesforceFileAnswer;
};

type Props = {
  /** Link the persisted dataset to this assessment response set, when imported inside an assessment. */
  responseSetId?: number | null;
  updatedBy?: string | null;
  /** Called after a successful save with the persisted reference (store this as the answer). */
  onSaved?: (ref: SavedRef, widgets: WidgetSpec[]) => void;
  /** A previously-saved reference, to show a "saved" state on re-open. */
  initialSaved?: SavedRef | null;
  disabled?: boolean;
  /**
   * Salesforce AccountId to attach the raw uploaded file to (as a Salesforce File), when the import
   * is coupled to an `sf_account` answer. Null → the file is parsed into widgets but not uploaded.
   */
  accountId?: string | null;
};

const CHART_TYPES: { value: WidgetChartType; label: string }[] = [
  { value: 'vertical_bar_chart', label: 'Vertical Bar' },
  { value: 'horizontal_bar_chart', label: 'Horizontal Bar' },
  { value: 'donut_chart', label: 'Donut' },
  { value: 'pie_chart', label: 'Pie' },
  { value: 'datagrid', label: 'Table' },
];

const AGGREGATES: { value: GenericAggregate; label: string }[] = [
  { value: 'count', label: 'Count of rows' },
  { value: 'sum', label: 'Sum of value' },
  { value: 'avg', label: 'Average of value' },
];

const DatasetWidgetBuilder: React.FC<Props> = ({
  responseSetId,
  updatedBy,
  onSaved,
  initialSaved,
  disabled,
  accountId,
}) => {
  const [dataset, setDataset] = useState<ParsedDataset | null>(null);
  const [presetKey, setPresetKey] = useState<string | undefined>(undefined);
  const [widgets, setWidgets] = useState<WidgetSpec[]>([]);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<SavedRef | null>(initialSaved ?? null);
  // Non-blocking notice about the Salesforce file attachment (success or failure).
  const [sfNotice, setSfNotice] = useState<string | null>(null);

  // The raw uploaded file, kept so it can be attached to the Salesforce account on save. The
  // parsed dataset above intentionally does not retain the bytes.
  const rawFileRef = useRef<File | null>(null);
  // The Salesforce File already created for the current file, so "Save again" doesn't re-upload.
  const sfFileRef = useRef<SalesforceFileAnswer | null>(initialSaved?.salesforceFile ?? null);

  // Generic mapper state (only used when no preset claims the file).
  const [mapping, setMapping] = useState<GenericWidgetMapping>({
    title: '',
    chartType: 'vertical_bar_chart',
    labelColumn: '',
    valueColumn: '',
    aggregate: 'count',
  });

  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(async (file: File) => {
    setError(null);
    setParsing(true);
    setSaved(null);
    setSfNotice(null);
    // A new file supersedes any prior Salesforce attachment; keep the bytes for the upload on save.
    rawFileRef.current = file;
    sfFileRef.current = null;
    try {
      const parsed = await parseDatasetFile(file);
      setDataset(parsed);

      const { preset } = resolvePreset(parsed);
      if (preset) {
        setPresetKey(preset.key);
        setWidgets(preset.build(parsed).widgets);
      } else {
        setPresetKey(undefined);
        // Seed the generic mapper with the first column and start with just the raw table.
        setMapping((m) => ({ ...m, labelColumn: parsed.columns[0] || '', title: parsed.columns[0] || 'Chart' }));
        setWidgets([buildRawTableWidget(parsed)]);
      }
    } catch (e: any) {
      setDataset(null);
      setWidgets([]);
      setError(e?.message || 'Could not parse that file.');
    } finally {
      setParsing(false);
    }
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const file = e.dataTransfer.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  const addGenericWidget = () => {
    if (!dataset || !mapping.labelColumn) return;
    const spec = buildGenericWidget(dataset, {
      ...mapping,
      title: mapping.title || `${mapping.labelColumn} chart`,
    });
    // Replace an existing widget with the same id, else append.
    setWidgets((prev) => [...prev.filter((w) => w.id !== spec.id), spec]);
  };

  const removeWidget = (id: string) => setWidgets((prev) => prev.filter((w) => w.id !== id));

  const handleSave = async () => {
    if (!dataset || widgets.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      const result = await persistWidgets({
        datasetName: dataset.sourceMeta.fileName,
        sourceFileName: dataset.sourceMeta.fileName,
        sourceMeta: dataset.sourceMeta,
        preset: presetKey,
        columns: dataset.columns,
        rows: dataset.rows,
        widgets,
        responseSetId: responseSetId ?? null,
        updatedBy: updatedBy ?? null,
      });
      // Best-effort: also attach the raw file to the coupled Salesforce account. A failure here
      // must never fail the save — the parsed widgets are already persisted; no account → skip.
      if (accountId && rawFileRef.current && !sfFileRef.current) {
        try {
          const rec = await uploadCustomerFile(accountId, rawFileRef.current);
          sfFileRef.current = {
            account_id: accountId,
            content_document_id: rec.content_document_id,
            content_version_id: rec.content_version_id,
            file_name: rec.file_name,
          };
          setSfNotice(`Attached ${rec.file_name} to the Salesforce account.`);
        } catch (e: any) {
          setSfNotice(
            `Widgets saved, but attaching the file to Salesforce failed: ${
              e?.message || 'unknown error'
            }`,
          );
        }
      }

      const ref: SavedRef = {
        datasetId: result.datasetId,
        presetKey,
        tileIds: result.tiles.map((t) => t.tileId),
        widgetCount: widgets.length,
        ...(sfFileRef.current ? { salesforceFile: sfFileRef.current } : {}),
      };
      setSaved(ref);
      onSaved?.(ref, widgets);
    } catch (e: any) {
      setError(e?.message || 'Could not save the widgets.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      {/* Dropzone */}
      <Paper
        variant="outlined"
        onDragOver={(e) => e.preventDefault()}
        onDrop={disabled ? undefined : onDrop}
        onClick={() => !disabled && inputRef.current?.click()}
        sx={{
          p: 3,
          textAlign: 'center',
          cursor: disabled ? 'default' : 'pointer',
          borderStyle: 'dashed',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls,.xlsm,.csv"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
            e.target.value = '';
          }}
        />
        <UploadFile color="action" />
        <Typography variant="body2" sx={{ mt: 1 }}>
          {dataset
            ? `${dataset.sourceMeta.fileName} — ${dataset.rowCount} rows`
            : 'Drop an Excel or CSV file, or click to choose'}
        </Typography>
        {parsing && <CircularProgress size={18} sx={{ mt: 1 }} />}
      </Paper>

      {!!error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {dataset && (
        <Box mt={2}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            {presetKey ? (
              <Chip color="primary" size="small" label={`Preset: ${presetKey}`} />
            ) : (
              <Chip size="small" label="Generic mapping" />
            )}
            <Typography variant="caption" color="text.secondary">
              {dataset.columns.length} columns
            </Typography>
          </Stack>

          {/* Generic mapper — only when no preset claimed the file */}
          {!presetKey && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Add a chart
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={3}>
                  <TextField
                    select fullWidth size="small" label="Chart type"
                    value={mapping.chartType}
                    onChange={(e) => setMapping((m) => ({ ...m, chartType: e.target.value as WidgetChartType }))}
                  >
                    {CHART_TYPES.map((c) => (
                      <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    select fullWidth size="small" label="Group by (label)"
                    value={mapping.labelColumn}
                    onChange={(e) => setMapping((m) => ({ ...m, labelColumn: e.target.value }))}
                  >
                    {dataset.columns.map((c) => (
                      <MenuItem key={c} value={c}>{c}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    select fullWidth size="small" label="Aggregate"
                    value={mapping.aggregate}
                    onChange={(e) => setMapping((m) => ({ ...m, aggregate: e.target.value as GenericAggregate }))}
                  >
                    {AGGREGATES.map((a) => (
                      <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    select fullWidth size="small" label="Value column"
                    value={mapping.valueColumn || ''}
                    disabled={mapping.aggregate === 'count'}
                    onChange={(e) => setMapping((m) => ({ ...m, valueColumn: e.target.value }))}
                  >
                    {dataset.columns.map((c) => (
                      <MenuItem key={c} value={c}>{c}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth size="small" label="Widget title"
                    value={mapping.title}
                    onChange={(e) => setMapping((m) => ({ ...m, title: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    fullWidth variant="outlined"
                    disabled={!mapping.labelColumn}
                    onClick={addGenericWidget}
                    sx={{ height: '100%' }}
                  >
                    Add widget
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* Widget previews */}
          <Grid container spacing={2}>
            {widgets.map((spec, index) => (
              <Grid item xs={12} md={spec.chartType === 'topology' || spec.chartType === 'datagrid' ? 12 : 6} key={spec.id}>
                <Box sx={{ position: 'relative' }}>
                  {!disabled && (
                    <Button
                      size="small"
                      color="inherit"
                      onClick={() => removeWidget(spec.id)}
                      sx={{ position: 'absolute', top: 4, right: 4, zIndex: 1, minWidth: 0 }}
                    >
                      Remove
                    </Button>
                  )}
                  <WidgetRenderer spec={spec} index={index} height={spec.chartType === 'topology' ? 420 : 300} />
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Save */}
          <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2} mt={2}>
            {sfNotice && (
              <Typography
                variant="caption"
                color={sfNotice.includes('failed') ? 'warning.main' : 'success.main'}
              >
                {sfNotice}
              </Typography>
            )}
            {saved && (
              <Typography variant="caption" color="success.main">
                Saved — {saved.widgetCount} widget(s), dataset #{saved.datasetId}
              </Typography>
            )}
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={disabled || saving || widgets.length === 0}
            >
              {saving ? 'Saving…' : saved ? 'Save again' : 'Save widgets'}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DatasetWidgetBuilder;
